import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { HelpCircle } from 'lucide-react';
import ChatMessage from './ChatMessage';
import SampleQuestions from './SampleQuestions';

const ChatInterface = ({ isOnline = true, initialSessionId = null, isGroupChat = false, isSharedSession = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sampleQuestions, setSampleQuestions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionId, setSessionId] = useState(initialSessionId || urlSessionId || null);
  const messagesEndRef = useRef(null);

  // Right panel tab state - default to 'history' for shared sessions (Chat History pre-selected)
  const [rightPanelTab, setRightPanelTab] = useState(isSharedSession ? 'history' : 'samples');
  
  // Chat history states
  const [chatHistory, setChatHistory] = useState({ group_sessions: [], private_sessions: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [viewingHistorySession, setViewingHistorySession] = useState(null);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // Shared session states
  const [sharedSessionInfo, setSharedSessionInfo] = useState(null);
  const [hasJoinedSharedSession, setHasJoinedSharedSession] = useState(false);
  const [joiningSession, setJoiningSession] = useState(false);
  const [loadingSharedSession, setLoadingSharedSession] = useState(isSharedSession);
  
  // Track if current session is a group chat (for persistence)
  const [isCurrentSessionGroup, setIsCurrentSessionGroup] = useState(false);

  // Email Composer States for Generated Reports
  const [showReportEmailComposer, setShowReportEmailComposer] = useState(false);
  const [reportEmailData, setReportEmailData] = useState({
    to: '',
    subject: 'Financial Report from Finsight Ops',
    body: 'Hi,\n\nPlease find attached the requested financial report generated by Finsight Ops for your review and analysis.\n\nThis report contains key insights on loan portfolio, interest performance, credit exposure, and risk metrics.\n\nIf you have any questions or need additional insights, please let us know.\n\nBest regards,\nFinsight Ops',
    pdfBase64: '',
    filename: ''
  });
  
  // Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Sample queries popover (next to input)
  const [showSampleQueriesPopover, setShowSampleQueriesPopover] = useState(false);
  const sampleQueriesPopoverRef = useRef(null);

  // Profile dropdown (header)
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef(null);

  // Inline edit session title
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  // Card three-dots menu (which session's menu is open)
  const [cardMenuSessionId, setCardMenuSessionId] = useState(null);

  // Chat history sections: expand/collapse (Group Chats collapsed by default)
  const [groupChatsExpanded, setGroupChatsExpanded] = useState(false);
  const [yourChatsExpanded, setYourChatsExpanded] = useState(true);

  // Sidebar collapse (desktop): collapsed = narrow strip on the left, expand = full width
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Show success toast
  const showSuccessToast = (message) => {
    setToastMessage(message);
    setToastType('success');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Show error toast
  const showErrorToast = (message) => {
    setToastMessage(message);
    setToastType('error');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  // Fetch shared session info
  const fetchSharedSessionInfo = async () => {
    if (!urlSessionId) return;
    
    try {
      setLoadingSharedSession(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${urlSessionId}/info`);
      
      if (response.data.success) {
        setSharedSessionInfo(response.data);
        setSessionId(urlSessionId);
        const isMember = response.data.members.some(m => m.user_id === user?.id);
        const sessionTitle = response.data.session?.title || response.data.session?.session_title || 'Group Chat';
        const isGroup = response.data.session?.session_type === 'GROUP';
        if (isMember) {
          setHasJoinedSharedSession(true);
          setIsCurrentSessionGroup(isGroup);
          loadHistorySession(urlSessionId, sessionTitle, isGroup);
        }
      } else {
        // Only show error if we're actually trying to load a shared session
        if (urlSessionId && isSharedSession) {
          showErrorToast('Failed to load shared session');
        }
      }
    } catch (err) {
      console.error('Error fetching shared session info:', err);
      // Only show error toast if we're actually trying to load a shared session URL
      if (urlSessionId && isSharedSession) {
        showErrorToast('Failed to load shared session');
      }
    } finally {
      setLoadingSharedSession(false);
    }
  };

  // Join shared session
  const handleJoinSharedSession = async () => {
    if (!urlSessionId || !user?.id) return;
    
    try {
      setJoiningSession(true);
      
      // Check if already a member first
      const isMember = sharedSessionInfo?.members?.some(m => m.user_id === user?.id);
      
      if (!isMember) {
        // Join the session if not already a member
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${urlSessionId}/join`, {
          session_id: urlSessionId,
          user_id: user.id
        });
        
        if (!response.data.success) {
          showErrorToast('Failed to join chat');
          return;
        }
      }
      
      // Now load the chat messages
      setHasJoinedSharedSession(true);
      setIsCurrentSessionGroup(true);
      showSuccessToast('Successfully joined the group chat!');
      loadHistorySession(urlSessionId, sharedSessionInfo?.session?.title || 'Group Chat', true);
      
      // Persist group session
      localStorage.setItem('finsight_group_session', JSON.stringify({
        sessionId: urlSessionId,
        title: sharedSessionInfo?.session?.title || 'Group Chat'
      }));
      
      // Refresh chat history to show the newly joined group chat
      fetchChatHistory();
      
    } catch (err) {
      console.error('Error joining session:', err);
      showErrorToast(err.response?.data?.detail || 'Failed to join chat');
    } finally {
      setJoiningSession(false);
    }
  };

  // Create chat session on component mount (or when starting new chat). Returns new session_id when created.
  const createChatSession = async (forceNew = false) => {
    // Prevent creating multiple sessions unless forced
    if (!forceNew && sessionId) {
      console.log('Session already exists:', sessionId);
      // If we have an initial session (from join), load its messages
      if (initialSessionId) {
        loadHistorySession(initialSessionId, 'Group Chat', true);
      }
      return null;
    }
    
    try {
      const userId = user?.id;
      if (!userId) {
        console.error('No user ID available');
        return null;
      }
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session`, {
        user_id: userId
      });
      const newSessionId = response.data.session_id;
      const sessionTitle = response.data.session_title || 'New Chat';
      setSessionId(newSessionId);
      setViewingHistorySession({ id: newSessionId, title: sessionTitle });
      console.log('Chat session created:', newSessionId, 'Title:', sessionTitle);

      // Warm up LLM so first query does not hit cold connection (avoids ConnectError)
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chat/warmup`).catch(() => {});
      
      // Refresh chat history after creating new session
      fetchChatHistory();
      // Don't update URL for fresh sessions - URL should only contain session ID when opening from history
      return newSessionId;
    } catch (error) {
      console.error('Error creating chat session:', error);
      return null;
    }
  };

  // Handle add member button click - show share dialog
  const handleAddMember = async () => {
    if (!sessionId) {
      showErrorToast('No active session to share');
      return;
    }
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${sessionId}/share`);
      
      if (response.data.success) {
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/join/${sessionId}`;
        setShareLink(link);
        setShowShareDialog(true);
        setLinkCopied(false);
        
        // Mark as group chat and persist session ID
        setIsCurrentSessionGroup(true);
        localStorage.setItem('finsight_group_session', JSON.stringify({
          sessionId: sessionId,
          title: viewingHistorySession?.title || 'Group Chat'
        }));
        
        // Refresh chat history as session may have changed to group type
        fetchChatHistory();
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      showErrorToast('Failed to generate share link');
    }
  };

  // Copy share link to clipboard
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      showSuccessToast('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showErrorToast('Failed to copy link');
    }
  };

  // Track if session creation is in progress to prevent duplicate calls
  const sessionCreationRef = useRef(false);

  useEffect(() => {
    // IMPORTANT: Never create a new session for shared session URLs
    // This prevents auto-joining when opening a shared link
    if (isSharedSession) {
      if (urlSessionId) {
        fetchSharedSessionInfo();
        fetchSampleQuestions();
      }
      sessionCreationRef.current = true;
      return;
    }
    
    // If we have an initial session ID (from join), use it
    if (initialSessionId) {
      setSessionId(initialSessionId);
      loadHistorySession(initialSessionId, 'Group Chat', true);
      sessionCreationRef.current = true;
      return;
    }
    
    // Prevent duplicate session creation in React Strict Mode
    if (sessionCreationRef.current) {
      return;
    }
    sessionCreationRef.current = true;
    
    createChatSession();
    fetchSampleQuestions();
    
    // Cleanup function
    return () => {
      // Don't reset the ref on cleanup to prevent re-creation
    };
  }, [isSharedSession, urlSessionId]);

  // Refresh chat history periodically so that when another user joins a shared session,
  // the primary user sees the chat appear in the Group Chats section without reloading.
  const CHAT_HISTORY_POLL_INTERVAL_MS = 5000; // 15 seconds
  useEffect(() => {
    if (!user?.id || !sessionId || isSharedSession) return;
    const intervalId = setInterval(() => {
      fetchChatHistory(true); // silent: no loading UI during background refresh
    }, CHAT_HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [user?.id, sessionId, isSharedSession]);

  // Group chat: poll messages so all members see new messages without refreshing.
  // Only replace when server has *more* messages: never overwrite with fewer (keeps optimistic
  // user message visible), and never replace when same length (avoids refetch flicker).
  const GROUP_CHAT_MESSAGES_POLL_MS = 3000;
  useEffect(() => {
    if (!sessionId || !isCurrentSessionGroup) return;
    if (isSharedSession && !hasJoinedSharedSession) return;
    const intervalId = setInterval(async () => {
      if (loading) return; // don't overwrite while sending
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${sessionId}/messages`);
        if (res.data.success && Array.isArray(res.data.messages)) {
          const next = formatMessagesFromApi(res.data.messages);
          setMessages(prev => {
            if (next.length > prev.length) return next;
            return prev;
          });
        }
      } catch (_) { /* ignore */ }
    }, GROUP_CHAT_MESSAGES_POLL_MS);
    return () => clearInterval(intervalId);
  }, [sessionId, isCurrentSessionGroup, isSharedSession, hasJoinedSharedSession, loading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close sample queries popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sampleQueriesPopoverRef.current && !sampleQueriesPopoverRef.current.contains(e.target)) {
        setShowSampleQueriesPopover(false);
      }
    };
    if (showSampleQueriesPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSampleQueriesPopover]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  // Close card menu when clicking outside the menu or its trigger
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!cardMenuSessionId) return;
      if (e.target.closest('.card-menu-dropdown') || e.target.closest('.card-menu-trigger')) return;
      setCardMenuSessionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cardMenuSessionId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSampleQuestions = async () => {
    try {
      const response = await axios.get('/sample-questions');
      setSampleQuestions(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching sample questions:', error);
    }
  };

  // Fetch chat history for the logged in user.
  // When silent is true (e.g. background refresh), no loading state is shown on the UI.
  const fetchChatHistory = async (silent = false) => {
    if (!user || !user.id) return;
    
    if (!silent) setLoadingHistory(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/sessions/${user.id}?limit=10`);
      
      if (response.data.success) {
        setChatHistory({
          group_sessions: response.data.group_sessions || [],
          private_sessions: response.data.private_sessions || []
        });
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      if (!silent) setLoadingHistory(false);
    }
  };

  // Format API messages to chat UI shape (shared by loadHistorySession and group chat poll)
  const formatMessagesFromApi = (apiMessages) => {
    if (!apiMessages || !Array.isArray(apiMessages)) return [];
    const formatted = [];
    apiMessages.forEach(msg => {
      formatted.push({
        id: `${msg.id}-user`,
        type: 'user',
        content: msg.message,
        user_id: msg.user_id,
        userName: msg.user_name || msg.userName || `User ${msg.user_id}`,
        timestamp: new Date(msg.created_at)
      });
      if (msg.response) {
        formatted.push({
          id: `${msg.id}-assistant`,
          type: 'assistant',
          content: msg.response,
          charts: msg.charts || [],
          tables: msg.tables || [],
          summaryPoints: msg.summary_points || [],
          kpi_cards: msg.kpi_cards || [],
          timestamp: new Date(msg.created_at)
        });
      }
    });
    return formatted;
  };

  // Load messages for a specific session from history
  const loadHistorySession = async (historySessionId, sessionTitle, isGroupSession = false) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${historySessionId}/messages`);
      
      if (response.data.success) {
        const formattedMessages = formatMessagesFromApi(response.data.messages);
        setMessages(formattedMessages);
        setViewingHistorySession({ id: historySessionId, title: sessionTitle });
        setSessionId(historySessionId); // Set the session ID to continue the conversation
        setIsCurrentSessionGroup(isGroupSession);
        
        // Persist only if it's a group session
        if (isGroupSession) {
          localStorage.setItem('finsight_group_session', JSON.stringify({
            sessionId: historySessionId,
            title: sessionTitle
          }));
        } else {
          localStorage.removeItem('finsight_group_session');
        }
      }
    } catch (error) {
      console.error('Error loading history session:', error);
      showErrorToast('Failed to load chat history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUpdateSessionTitle = async (sessionIdToUpdate, newTitle) => {
    if (!newTitle?.trim()) return;
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${sessionIdToUpdate}/title`,
        { title: newTitle.trim() }
      );
      setEditingSessionId(null);
      setEditingTitle('');
      fetchChatHistory();
      if (viewingHistorySession?.id === sessionIdToUpdate) {
        setViewingHistorySession(prev => prev ? { ...prev, title: newTitle.trim() } : null);
      }
      showSuccessToast('Title updated');
    } catch (error) {
      console.error('Error updating session title:', error);
      showErrorToast(error.response?.data?.detail || 'Failed to update title');
    }
  };

  const handleDeleteSession = async (sessionIdToDelete, isGroup) => {
    if (!window.confirm('Delete this chat? This cannot be undone.')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${sessionIdToDelete}`);
      if (viewingHistorySession?.id === sessionIdToDelete) {
        setViewingHistorySession(null);
        setMessages([]);
        setSessionId(null);
        sessionCreationRef.current = false;
      }
      setEditingSessionId(null);
      setEditingTitle('');
      fetchChatHistory();
      showSuccessToast('Chat deleted');
    } catch (error) {
      console.error('Error deleting session:', error);
      showErrorToast(error.response?.data?.detail || 'Failed to delete chat');
    }
  };

  // Start a new chat session (same behavior as page reload: create new session and use its id for /chat API)
  const startNewChat = async () => {
    setViewingHistorySession(null);
    setMessages([]);
    sessionCreationRef.current = false;
    setIsCurrentSessionGroup(false);
    // Clear persisted group session when starting new chat
    localStorage.removeItem('finsight_group_session');
    // Navigate to /chat without session ID (fresh session)
    navigate('/chat', { replace: true });
    // Force create a new session and set its id so /chat API receives it (same as on initial load)
    const newSessionId = await createChatSession(true);
    if (newSessionId) {
      setSessionId(newSessionId);
    } else {
      setSessionId(null);
    }
    fetchChatHistory(); // Refresh history
  };

  // Fetch chat history on component mount
  useEffect(() => {
    fetchChatHistory();
  }, [user]);

  // Watch for URL session ID changes and load that session
  useEffect(() => {
    if (urlSessionId && !isSharedSession) {
      // URL has a session ID (user clicked from history)
      // Load that specific session
      const loadSessionFromUrl = async () => {
        try {
          // First, check if this session exists in history
          const historyResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/sessions/${user.id}?limit=50`);
          
          if (historyResponse.data.success) {
            const allSessions = [
              ...(historyResponse.data.group_sessions || []),
              ...(historyResponse.data.private_sessions || [])
            ];
            const session = allSessions.find(s => s.id === urlSessionId);
            
            if (session) {
              // Load this session
              const sessionTitle = session.title || session.session_title || 'Chat';
              const isGroup = session.session_type === 'GROUP';
              loadHistorySession(urlSessionId, sessionTitle, isGroup);
            } else {
              // Session not found, navigate back to fresh chat
              navigate('/chat', { replace: true });
            }
          }
        } catch (error) {
          console.error('Error loading session from URL:', error);
          // On error, navigate to fresh chat
          navigate('/chat', { replace: true });
        }
      };
      
      // Only load if user is available
      if (user?.id) {
        loadSessionFromUrl();
      }
    } else if (!urlSessionId && !isSharedSession) {
      // No session ID in URL - this is a fresh chat
      // Only clear if we're currently viewing a history session
      if (viewingHistorySession) {
        setViewingHistorySession(null);
        setMessages([]);
      }
    }
  }, [urlSessionId, isSharedSession, user?.id]);

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      user_id: user?.id,
      userName: user?.name || `User ${user?.id}`,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setSidebarOpen(false);

    try {
      const response = await axios.post('/chat', {
        message: message,
        user_id: user.id,
        session_id: sessionId
      });

      // Check if response contains an error message from backend
      const responseText = response.data.response || '';
      const isErrorResponse = responseText.toLowerCase().startsWith('error') || 
                              responseText.includes('validation error') ||
                              responseText.includes('Error getting AI response') ||
                              responseText.includes('Error analyzing data');

      const botMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: isErrorResponse 
          ? 'Unable to process your request. Please try again or rephrase your question.'
          : responseText,
        charts: isErrorResponse ? [] : (response.data.charts || []),
        tables: isErrorResponse ? [] : (response.data.tables || []),
        summaryPoints: isErrorResponse ? [] : (response.data.summary_points || []),
        kpi_cards: isErrorResponse ? [] : (response.data.kpi_cards || []),
        chartData: isErrorResponse ? null : response.data.chart_data,
        chartType: isErrorResponse ? null : response.data.chart_type,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Refresh chat history so the current chat appears immediately (e.g. first message updates session title)
      fetchChatHistory();
      // Update header title (backend may have set/updated session title on first message)
      try {
        const infoRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/session/${sessionId}/info`);
        if (infoRes.data.success && infoRes.data.session) {
          const title = infoRes.data.session.title || infoRes.data.session.session_title || 'New Chat';
          setViewingHistorySession(prev => (prev?.id === sessionId ? { ...prev, title } : { id: sessionId, title }));
        }
      } catch (_) { /* ignore */ }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: 'Unable to process your request. Please try again or rephrase your question.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle email report from ChatMessage component
  const handleEmailReport = ({ pdfBase64, filename }) => {
    setReportEmailData({
      to: '',
      subject: 'Financial Report from Finsight Ops',
      body: 'Hi,\n\nPlease find attached the requested financial report generated by Finsight Ops for your review and analysis.\n\nThis report contains key insights on properties, auctions and bids.\n\nIf you have any questions or need additional insights, please let us know.\n\nBest regards,\nFinsight Ops',
      pdfBase64,
      filename
    });
    setShowReportEmailComposer(true);
  };

  // Handle sending email with generated report
  const handleSendReportEmail = async () => {
    if (!reportEmailData.to.trim()) {
      showErrorToast('Please enter a recipient email address');
      return;
    }

    setIsSendingEmail(true);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/send-email-with-pdf`, {
        to_email: reportEmailData.to,
        subject: reportEmailData.subject,
        body: reportEmailData.body,
        pdf_base64: reportEmailData.pdfBase64,
        pdf_filename: reportEmailData.filename
      });

      if (response.data.success) {
        showSuccessToast(response.data.message || 'Email sent successfully!');
        setShowReportEmailComposer(false);
      } else {
        showErrorToast(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send email';
      showErrorToast(errorMessage);
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="h-screen bg-slate-100 flex">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-[100] animate-slide-in">
          <div className={`${toastType === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}>
            {toastType === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Left sidebar - full height from top; collapses to narrow strip on the left */}
      <div className={`sidebar-modern flex-shrink-0 md:block ${sidebarOpen ? 'block' : 'hidden'} md:relative md:translate-x-0 h-full transition-[width] duration-200 ease-in-out ${sidebarCollapsed ? 'w-12' : 'w-80'}`}>
        <div className="h-full flex flex-col bg-slate-50 border-r border-slate-200 overflow-hidden">
          <div className="flex-shrink-0 border-b border-slate-200 bg-white">
            <div className={`flex items-center justify-between min-h-[40px] ${sidebarCollapsed ? 'justify-center px-0' : 'px-4'}`}>
              {!sidebarCollapsed && (
                <>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="md:hidden p-1 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="hidden md:block text-slate-800 font-semibold text-lg">
                    Finsight Ops
                  </div>
                </>
              )}
              {!sidebarCollapsed && <div className="flex-1" />}
              <button
                type="button"
                onClick={() => setSidebarCollapsed(prev => !prev)}
                className="hidden md:flex p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <rect x="3" y="5" width="10" height="14" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 12H21M17 12L19 10M17 12L19 14" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <rect x="3" y="5" width="10" height="14" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 12H21M17 12L19 10M17 12L19 14" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className={`flex-1 min-h-0 min-w-0 ${sidebarCollapsed ? 'overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden' : 'flex flex-col'}`}>
            {sidebarCollapsed ? (
              /* Collapsed: icon-only strip with tooltips */
              <div className="flex flex-col items-center py-3 gap-3">
                <button
                  type="button"
                  onClick={() => { setSidebarCollapsed(false); startNewChat(); }}
                  className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                  title="New Chat"
                  aria-label="New Chat"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarCollapsed(false)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                  title="Chat history"
                  aria-label="Chat history"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </button>
              </div>
            ) : (
            <>
              {/* New Chat button - fixed, does not scroll */}
              <div className="flex-shrink-0 p-3">
                <button
                  onClick={startNewChat}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Chat
                </button>
              </div>
              {/* Chat history only - scrollable */}
              <div className="flex-1 overflow-y-auto min-h-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-3 pt-0">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-slate-500">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Loading history...</span>
                  </div>
                </div>
              ) : (chatHistory.group_sessions?.length === 0 && chatHistory.private_sessions?.length === 0) ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-sm text-slate-500">No chat history yet</p>
                  <p className="text-xs text-slate-400 mt-1">Start a conversation to see it here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatHistory.group_sessions?.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setGroupChatsExpanded(prev => !prev)}
                        className="flex items-center gap-2 mb-2 w-full text-left rounded-lg py-1 -mx-1 px-1 hover:bg-slate-100 transition-colors"
                        aria-expanded={groupChatsExpanded}
                      >
                        <svg className={`w-4 h-4 text-purple-600 flex-shrink-0 transition-transform ${groupChatsExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Group Chats</h4>
                        <span className="text-xs text-slate-400 ml-auto">({chatHistory.group_sessions.length})</span>
                      </button>
                      {groupChatsExpanded && (
                      <div className="space-y-1.5">
                        {chatHistory.group_sessions.map((session) => (
                          <div
                            key={session.id}
                            className={`group flex items-center gap-2 w-full text-left py-2 px-2.5 rounded-lg border transition-all hover:shadow-sm ${
                              viewingHistorySession?.id === session.id
                                ? 'bg-purple-50 border-purple-200'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {editingSessionId === session.id ? (
                              <div className="flex-1 min-w-0 flex items-center gap-1" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                                <input
                                  value={editingTitle}
                                  onChange={e => setEditingTitle(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleUpdateSessionTitle(session.id, editingTitle); if (e.key === 'Escape') { setEditingSessionId(null); setEditingTitle(''); } }}
                                  className="flex-1 min-w-0 text-sm font-medium text-slate-800 border border-slate-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={() => handleUpdateSessionTitle(session.id, editingTitle)}
                                  title="Save"
                                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded flex-shrink-0"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => navigate(`/chat/${session.id}`)}
                                className="flex-1 min-w-0 flex flex-col items-start text-left"
                              >
                                <p className={`text-sm font-medium truncate w-full ${
                                  viewingHistorySession?.id === session.id ? 'text-purple-800' : 'text-slate-700'
                                }`}>
                                  {session.title}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {session.last_message_at
                                    ? new Date(session.last_message_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                  }
                                </p>
                              </button>
                            )}
                            {editingSessionId !== session.id && (
                              <div className="relative flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); setCardMenuSessionId(cardMenuSessionId === session.id ? null : session.id); }}
                                  title="More options"
                                  className="card-menu-trigger p-1 text-slate-400 hover:text-slate-600 rounded transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                  </svg>
                                </button>
                                {cardMenuSessionId === session.id && (
                                  <div className="card-menu-dropdown absolute right-0 top-full mt-0.5 py-1 w-36 bg-white rounded-lg border border-slate-200 shadow-lg z-50">
                                    <button
                                      type="button"
                                      onClick={e => { e.stopPropagation(); setCardMenuSessionId(null); setEditingSessionId(session.id); setEditingTitle(session.title); }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={e => { e.stopPropagation(); setCardMenuSessionId(null); handleDeleteSession(session.id, true); }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      )}
                    </div>
                  )}
                  {chatHistory.private_sessions?.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setYourChatsExpanded(prev => !prev)}
                        className="flex items-center gap-2 mb-2 w-full text-left rounded-lg py-1 -mx-1 px-1 hover:bg-slate-100 transition-colors"
                        aria-expanded={yourChatsExpanded}
                      >
                        <svg className={`w-4 h-4 text-emerald-600 flex-shrink-0 transition-transform ${yourChatsExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Your Chats</h4>
                        <span className="text-xs text-slate-400 ml-auto">({chatHistory.private_sessions.length})</span>
                      </button>
                      {yourChatsExpanded && (
                      <div className="space-y-1.5">
                        {chatHistory.private_sessions.map((session) => (
                          <div
                            key={session.id}
                            className={`group flex items-center gap-2 w-full text-left py-2 px-2.5 rounded-lg border transition-all hover:shadow-sm ${
                              viewingHistorySession?.id === session.id
                                ? 'bg-emerald-50 border-emerald-200'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {editingSessionId === session.id ? (
                              <div className="flex-1 min-w-0 flex items-center gap-1" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                                <input
                                  value={editingTitle}
                                  onChange={e => setEditingTitle(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleUpdateSessionTitle(session.id, editingTitle); if (e.key === 'Escape') { setEditingSessionId(null); setEditingTitle(''); } }}
                                  className="flex-1 min-w-0 text-sm font-medium text-slate-800 border border-slate-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={() => handleUpdateSessionTitle(session.id, editingTitle)}
                                  title="Save"
                                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded flex-shrink-0"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => navigate(`/chat/${session.id}`)}
                                className="flex-1 min-w-0 flex flex-col items-start text-left"
                              >
                                <p className={`text-sm font-medium truncate w-full ${
                                  viewingHistorySession?.id === session.id ? 'text-emerald-800' : 'text-slate-700'
                                }`}>
                                  {session.title}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {session.last_message_at
                                    ? new Date(session.last_message_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                  }
                                </p>
                              </button>
                            )}
                            {editingSessionId !== session.id && (
                              <div className="relative flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); setCardMenuSessionId(cardMenuSessionId === session.id ? null : session.id); }}
                                  title="More options"
                                  className="card-menu-trigger p-1 text-slate-400 hover:text-slate-600 rounded transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                  </svg>
                                </button>
                                {cardMenuSessionId === session.id && (
                                  <div className="card-menu-dropdown absolute right-0 top-full mt-0.5 py-1 w-36 bg-white rounded-lg border border-slate-200 shadow-lg z-50">
                                    <button
                                      type="button"
                                      onClick={e => { e.stopPropagation(); setCardMenuSessionId(null); setEditingSessionId(session.id); setEditingTitle(session.title); }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={e => { e.stopPropagation(); setCardMenuSessionId(null); handleDeleteSession(session.id, false); }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      {/* Chat window: header + content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Header - within chat window only */}
      <header className="bg-slate-900 border-b border-slate-800 flex-shrink-0 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                title="Back to Dashboard"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            </div>

            <div className="flex-1 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div>
                  <h1 className="text-lg font-bold text-white tracking-tight truncate max-w-[200px] sm:max-w-xs md:max-w-sm" title={viewingHistorySession?.title ?? 'New Chat'}>
                    {viewingHistorySession?.title ?? 'New Chat'}
                  </h1>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3" ref={profileDropdownRef}>
              {/* Profile icon - opens dropdown with logout */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                  title="Account"
                  aria-label="Account menu"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center ring-2 ring-slate-700 hover:ring-slate-500 transition-all">
                    <span className="text-white text-sm font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </button>
                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-56 py-1 bg-white rounded-xl border border-slate-200 shadow-xl z-50">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-xs text-slate-500">Logged in as</p>
                      <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="sidebar-toggle"
                className="md:hidden flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                title="Toggle Questions"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Chat area - takes remaining space but respects sidebar width */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto p-4 space-y-6" data-testid="chat-messages">
            {/* Show join prompt when accessing via shared link (/join/:sessionId) and user is not already a member */}
            {isSharedSession && urlSessionId && !loadingSharedSession && sharedSessionInfo && !hasJoinedSharedSession && !(sharedSessionInfo?.members?.some(m => m.user_id === user?.id)) ? (
              <div className="flex items-center justify-center h-full fade-in">
                <div className="max-w-md mx-auto text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">Group Chat Invitation</h2>
                  <p className="text-slate-500 mb-6">
                    You've been invited to join this group chat. Click below to view the conversation and participate with other members.
                  </p>
                  {loadingSharedSession ? (
                    <div className="bg-slate-100 rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading session info...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-100 rounded-xl p-4 mb-6">
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">Session:</span> {sharedSessionInfo?.session?.title || 'Group Chat'}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        {sharedSessionInfo?.member_count || 0} member{sharedSessionInfo?.member_count !== 1 ? 's' : ''} in this chat
                      </p>
                    </div>
                  )}
                  <button
                    onClick={handleJoinSharedSession}
                    disabled={joiningSession || loadingSharedSession}
                    data-testid="join-group-chat-btn"
                    className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {joiningSession ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Joining...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        <span>Join Group Chat</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-16 fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V5a2 2 0 00-2-2M5 3a2 2 0 00-2 2v6a2 2 0 002 2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">Asset Portfolio Overview</h3>
                    <p className="text-sm text-slate-500">Inventory, locations & asset categories</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">Asset Performance & Utilization</h3>
                    <p className="text-sm text-slate-500">Utilization rates, uptime & efficiency</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">Asset Lifecycle & Maintenance</h3>
                    <p className="text-sm text-slate-500">Depreciation, maintenance & retirement</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">Asset Valuation & Reporting</h3>
                    <p className="text-sm text-slate-500">Book value, depreciation & asset metrics</p>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <ChatMessage 
                  key={message.id} 
                  message={message} 
                  onEmailReport={handleEmailReport}
                  currentUserId={user?.id}
                  currentUserName={user?.name}
                />
              ))
            )}
            
            {loading && (
              <div className="flex justify-start fade-in">
                <div className="bg-white rounded-xl border border-slate-200 p-4 max-w-sm shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                    </div>
                    <span className="text-slate-600 text-sm">Analyzing asset data...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="bg-white border-t border-slate-200 shadow-lg flex-shrink-0">
            <div className="max-w-4xl mx-auto p-3 pl-4 pr-6">
              {/* Show disabled input only when we've confirmed it's a group invite and user hasn't joined */}
              {urlSessionId && !loadingSharedSession && sharedSessionInfo?.session?.session_type === 'GROUP' && !(sharedSessionInfo?.members?.some(m => m.user_id === user?.id)) ? (
                <div className="text-center py-3 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-sm text-slate-500">Join the group chat to send messages</p>
                </div>
              ) : (
                <div className="flex space-x-3 items-center mr-4" ref={sampleQueriesPopoverRef}>
                  {/* Add Member Button - to the left of input (group chat: share link to invite others) */}
                  <button
                    onClick={handleAddMember}
                    data-testid="add-member-btn"
                    className="h-12 w-12 flex-shrink-0 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </button>
                  {/* Input field */}
                  <div className="flex-1 relative flex items-center">
                    <textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      data-testid="chat-input"
                      placeholder="Ask about asset inventory, utilization, lifecycle, valuation..."
                      className="w-full min-h-12 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
                      rows="1"
                      disabled={loading}
                    />
                  </div>
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={loading || !inputMessage.trim()}
                    data-testid="chat-send-btn"
                    className="h-12 px-4 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    {loading ? (
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                    ) : (
                      <>
                        <svg className="w-5 h-5 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span>Send</span>
                      </>
                    )}
                  </button>
                  {/* Sample Queries trigger + popover (right of Send) */}
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowSampleQueriesPopover(!showSampleQueriesPopover)}
                      title="Suggested Questions"
                      className="h-12 w-12 flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-emerald-600 hover:border-emerald-200 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      aria-label="Sample Queries"
                    >
                      <HelpCircle className="w-6 h-6" />
                    </button>
                    {showSampleQueriesPopover && (
                      <div className="absolute bottom-full right-0 mb-2 w-80 max-h-[80vh] overflow-hidden bg-white rounded-xl border border-slate-200 shadow-xl z-50 flex flex-col">
                        <div className="p-3 border-b border-slate-100 bg-slate-50 flex-shrink-0">
                          <h3 className="font-semibold text-sm text-slate-800">Sample Queries</h3>
                          <p className="text-xs text-slate-500 mt-0.5">Click a query to send it</p>
                        </div>
                        <div className="overflow-y-auto flex-1 p-2 min-h-0">
                          {sampleQuestions.length === 0 ? (
                            <p className="text-xs text-slate-500 py-4 text-center">No sample queries available</p>
                          ) : (
                            sampleQuestions.map((category, categoryIndex) => (
                              <div key={category.id ?? categoryIndex} className="mb-3 last:mb-0">
                                {category.title !== 'Loan Portfolio' && (
                                  <p className="text-xs font-medium text-slate-500 px-2 py-1 truncate">{category.title}</p>
                                )}
                                <div className="space-y-0.5">
                                  {(category.questions || []).map((question, questionIndex) => (
                                    <button
                                      key={questionIndex}
                                      type="button"
                                      onClick={() => {
                                        setShowSampleQueriesPopover(false);
                                        handleSendMessage(question);
                                      }}
                                      disabled={loading}
                                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-emerald-50 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {question}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                {/* Prepare Report Button - HIDDEN/COMMENTED OUT */}
                {/*
                <button
                  onClick={handlePrepareReportClick}
                  data-testid="prepare-report-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white border-none rounded-lg px-3 py-2 font-medium transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md flex items-center space-x-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm">Prepare Report</span>
                </button>
                */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Sidebar overlay - temporarily simplified */}
      {sidebarOpen ? (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      {/* Report Email Composer Modal */}
      {showReportEmailComposer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            {/* Email Header */}
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-cyan-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Send Financial Report</h3>
                    <p className="text-sm text-slate-500">Finsight Ops Report</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReportEmailComposer(false)}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Email Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* To Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">To</label>
                <input
                  type="email"
                  value={reportEmailData.to}
                  onChange={(e) => setReportEmailData(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="recipient@institution.com"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>

              {/* Subject Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={reportEmailData.subject}
                  onChange={(e) => setReportEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>

              {/* Body Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea
                  value={reportEmailData.body}
                  onChange={(e) => setReportEmailData(prev => ({ ...prev, body: e.target.value }))}
                  rows="6"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
                />
              </div>

              {/* Attachment Info */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-emerald-900 text-sm">{reportEmailData.filename}</p>
                    <p className="text-xs text-emerald-600">Financial report (PDF)</p>
                  </div>
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Email Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex space-x-3">
              <button
                onClick={() => setShowReportEmailComposer(false)}
                disabled={isSendingEmail}
                className="flex-1 py-3 px-4 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReportEmail}
                disabled={!reportEmailData.to.trim() || isSendingEmail}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white rounded-xl font-medium transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSendingEmail ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span>Send Email</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>

      {/* Share Link Dialog */}
      {showShareDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-cyan-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Share Chat Session</h3>
                    <p className="text-sm text-slate-500">Invite others to join this chat</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowShareDialog(false)}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Share this link with others to invite them to join this chat session. Once they join, it will become a group chat.
              </p>
              
              <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-xl">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-slate-700 outline-none"
                />
                <button
                  onClick={copyShareLink}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    linkCopied 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                >
                  {linkCopied ? (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </span>
                  )}
                </button>
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-amber-700">
                    Anyone with this link can join this chat session. They must be logged in to join.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setShowShareDialog(false)}
                className="w-full px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
